// src/sim/round/spawn-placement.ts — P10 Plan 01 seeded spawn-layout module (D-01, D-02, D-04).
//
// Purpose: shared contract consumed by GameScene (round-1 spawn) and team-switch (round-2+
// re-placement). Isolates all placement math in a Phaser-free sim module, keeping the
// sim/render split honest and making the seeded assignment unit-testable.
//
// Invariants:
//   - Pure: returns new values; never mutates inputs; no global state.
//   - No Phaser / Howler / React imports — this file lives in src/sim/**.
//   - All randomness through hashSeed + Alea; Math.random and Date.now are banned.
//   - computeSpawnLayout does NOT snap to tiles — tile-snapping is the caller's render-side
//     concern (GameScene.ts has snapToPassableTile; this module has no grid access).
//
// Source: .planning/phases/10-full-4v4-roster/10-01-PLAN.md

import Alea from 'alea';

import type { Vec2, ZoneId, ZonePolygon } from '../../shared/types/map';
import { zoneCenter } from '../map/zones';
import { hashSeed } from '../rng';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * The four named outer zones that hunters are spread across on round start (D-01).
 * Order is not significant — the per-round seed determines which hunter lands where.
 */
export const HUNTER_ZONE_NAMES = [
  'threshing-floor',
  'top-houses',
  'dulindas',
  'stable',
] as const;

export type HunterZoneName = (typeof HUNTER_ZONE_NAMES)[number];

// ─── makeSpawnRng ─────────────────────────────────────────────────────────────

/**
 * Create a per-round spawn RNG stream.
 *
 * Salting with roundNumber (D-02) ensures the hunter→zone assignment is deterministic
 * for a given round but varies between rounds. Downstream callers (GameScene, team-switch)
 * pass `state.round.roundNumber` or the next round number as appropriate.
 *
 * @param rngSeed - root world seed (WorldState.rngSeed)
 * @param roundNumber - 1-based round number (D-02: varies per round)
 * @returns An Alea-backed () => number RNG function for this round's spawn decisions.
 */
export function makeSpawnRng(rngSeed: number, roundNumber: number): () => number {
  return Alea(hashSeed(rngSeed, `spawn:round:${roundNumber}`));
}

// ─── computeSpawnLayout ───────────────────────────────────────────────────────

export interface SpawnLayoutOpts {
  /** Entity ids for the hunter team (any count; only first 4 get outer zones). */
  hunterIds: string[];
  /** Entity ids for the hider team. */
  hiderIds: string[];
  /** Zone polygons keyed by zone id. May be null/undefined in minimal test fixtures. */
  zones: Map<ZoneId, ZonePolygon> | null | undefined;
  /** Fallback spawn when a zone is absent from the map. Falls back to origin if also null. */
  playerSpawn?: Vec2 | null;
  /** Per-round RNG — obtain via makeSpawnRng(rngSeed, roundNumber). */
  rng: () => number;
}

/**
 * Fisher-Yates in-place shuffle of a copied array using the provided RNG.
 * Returns a new array; the input is not mutated.
 */
function shuffled<T>(arr: readonly T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

/**
 * Compute a flat spawn-position map for all hunters and hiders.
 *
 * Hunters (D-01 / D-02):
 *   - Sorts hunter ids alphabetically (stable, deterministic assignment order —
 *     same idiom as tick.ts L905 Object.keys().sort()).
 *   - Fisher-Yates shuffles a copy of HUNTER_ZONE_NAMES using opts.rng (the
 *     per-round seeded stream). This means the hunter→zone binding varies each
 *     round but is deterministic for the same seed+round pair (D-02).
 *   - Assigns shuffled zone[i] to sorted hunter[i].
 *   - Position = zoneCenter(zone) when the zone is present; opts.playerSpawn ?? {x:0,y:0} otherwise.
 *
 * Hiders (D-04):
 *   - All hiders anchor to zoneCenter('plinth'); fallback chain is playerSpawn → origin.
 *   - Each hider receives a deterministic {x: index*8, y: 0} anti-overlap offset (same
 *     spawnOffsetFor idiom as team-switch.ts).
 *
 * Returns a plain Record<entityId, Vec2>; tile-snapping is the caller's responsibility.
 */
export function computeSpawnLayout(opts: SpawnLayoutOpts): Record<string, Vec2> {
  const { hunterIds, hiderIds, zones, rng } = opts;
  const fallback: Vec2 = opts.playerSpawn ?? { x: 0, y: 0 };
  const result: Record<string, Vec2> = {};

  // ── Hunters: D-01 / D-02 ───────────────────────────────────────────────────
  // Stable alphabetical sort → deterministic assignment order independent of call-site ordering.
  const sortedHunterIds = [...hunterIds].sort();
  // Fisher-Yates shuffle of zone names with the per-round RNG → D-02 variation.
  const zoneOrder = shuffled(HUNTER_ZONE_NAMES, rng);

  for (let i = 0; i < sortedHunterIds.length; i++) {
    const hunterId = sortedHunterIds[i]!;
    const zoneName = zoneOrder[i];
    let pos: Vec2;
    if (zoneName !== undefined && zones) {
      const zone = zones.get(zoneName);
      pos = zone ? zoneCenter(zone) : fallback;
    } else {
      pos = fallback;
    }
    result[hunterId] = pos;
  }

  // ── Hiders: D-04 ───────────────────────────────────────────────────────────
  // All hiders cluster at the plinth zone center (break-from-base model).
  const plinthZone = zones?.get('plinth');
  const plinthCenter: Vec2 = plinthZone ? zoneCenter(plinthZone) : fallback;

  for (let i = 0; i < hiderIds.length; i++) {
    const hiderId = hiderIds[i]!;
    // {x: index*8, y: 0} anti-overlap offset — same pattern as team-switch spawnOffsetFor.
    result[hiderId] = {
      x: plinthCenter.x + i * 8,
      y: plinthCenter.y,
    };
  }

  return result;
}
